/**
 * Centralized number formatting utilities for Swedish locale
 */

// Swedish locale constant
const SWEDISH_LOCALE = 'sv-SE';

/**
 * Format a number with thousand separators using Swedish locale
 * @param value - The number to format
 * @returns Formatted string with thousand separators (e.g., "5 000")
 */
export const formatNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  return num.toLocaleString(SWEDISH_LOCALE);
};

/**
 * Format energy consumption in kWh with thousand separators
 * @param value - Energy value in kWh
 * @returns Formatted string (e.g., "5 000 kWh")
 */
export const formatKWh = (value: number | string): string => {
  const formatted = formatNumber(value);
  return `${formatted} kWh`;
};

/**
 * Format energy consumption with smart unit scaling (kWh/MWh/GWh)
 * @param value - Energy value in kWh
 * @param forceUnit - Force a specific unit instead of auto-scaling
 * @returns Formatted string with appropriate unit (e.g., "5 000 kWh", "5,0 MWh", "5,00 GWh")
 */
export const formatEnergyWithScaling = (value: number | string, forceUnit?: 'kWh' | 'MWh' | 'GWh'): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0 kWh';

  // Force specific unit if requested
  if (forceUnit === 'kWh') {
    return `${formatNumber(num)} kWh`;
  }
  if (forceUnit === 'MWh') {
    const mwh = num / 1000;
    return `${mwh.toLocaleString(SWEDISH_LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh`;
  }
  if (forceUnit === 'GWh') {
    const gwh = num / 1000000;
    return `${gwh.toLocaleString(SWEDISH_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`;
  }

  // Auto-scale based on value size
  if (num >= 1000000) {
    // Use GWh for values >= 1,000,000 kWh
    const gwh = num / 1000000;
    return `${gwh.toLocaleString(SWEDISH_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`;
  } else if (num >= 10000) {
    // Use MWh for values >= 10,000 kWh
    const mwh = num / 1000;
    return `${mwh.toLocaleString(SWEDISH_LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh`;
  } else {
    // Use kWh for smaller values
    return `${formatNumber(num)} kWh`;
  }
};

/**
 * Format energy consumption in GWh with thousand separators
 * @param value - Energy value in GWh
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "5 000,00 GWh")
 */
export const formatGWh = (value: number | string, decimals: number = 2): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00 GWh';
  
  const formatted = num.toLocaleString(SWEDISH_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${formatted} GWh`;
};

/**
 * Format currency amount in SEK with thousand separators
 * @param value - Amount in SEK
 * @returns Formatted string (e.g., "5 000,00 kr")
 */
export const formatCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00 kr';
  
  return new Intl.NumberFormat(SWEDISH_LOCALE, {
    style: 'currency',
    currency: 'SEK',
  }).format(num);
};

/**
 * Format currency amount in SEK with thousand separators (simple format)
 * @param value - Amount in SEK
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "5 000,00 kr")
 */
export const formatCurrencySimple = (value: number | string, decimals: number = 2): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00 kr';
  
  const formatted = num.toLocaleString(SWEDISH_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${formatted} kr`;
};

/**
 * Format percentage with Swedish locale
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string (e.g., "85,0 %")
 */
export const formatPercentage = (value: number | string, decimals: number = 1): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,0 %';
  
  const formatted = num.toLocaleString(SWEDISH_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${formatted} %`;
};

/**
 * Format compact numbers for charts and statistics
 * @param value - The number to format
 * @returns Formatted string with compact notation (e.g., "5K", "1,2M")
 */
export const formatCompactNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  
  return new Intl.NumberFormat(SWEDISH_LOCALE, {
    notation: 'compact',
  }).format(num);
};